import React, {useContext, useEffect, useState, useCallback, useRef, useMemo} from 'react'
import {useDispatch} from 'react-redux'
import _ from 'lodash'
import {Grid, withStyles, Button, Typography, Tooltip, CircularProgress} from '@material-ui/core'

import useModalState from '../../../hooks/useModalState'
import SurveyMultipleChoiceAnswer from './SurveyMultipleChoiceAnswer'
import SurveyOpenEndedAnswer from './SurveyOpenEndedAnswer'
import SurveyLanding from './SurveyLanding'
import {createError as createErrorAction} from '../../common/redux/actions.notifications'
import SurveyContext from '../context/SurveyContext'
import LoadingModal from '../LoadingModal'
import SurveyError from './SurveyError'
import {tagManagerEvent, eventTypes} from '../../campaign/tagManager'

import VideoApi from '../api'
import {questionSubTypes, questionTypes} from './SurveyStepConstants'
import SurveyFaultPercentageAnswer from './SurveyFaultPercentageAnswer'

import {surveyStates} from './constants'

const styles = theme => ({
  mainContainer: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    width: '25%',
    margin: '0 auto',
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      width: '90%',
    },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    textAlign: 'center',
    textTransform: 'initial',
    marginTop: 0,
    marginBottom: 25,
    fontWeight: 700,
    fontSize: 44,
    color: '#333333',
    [theme.breakpoints.down('sm')]: {
      fontSize: 30,
    },
  },
  surveyQuestionCounter: {
    margin: 20,
    color: '#4F4F4F',
    fontWeight: 500,
  },
  nextButton: {
    fontSize: 16,
    fontWeight: 500,
    borderRadius: 4,
    marginTop: '30px',
    padding: '10px 20px',
    background: '#017EFF',
    color: '#FFF',
    width: '100%',
    '&:hover': {
      backgroundColor: '#017EFF',
    },
    textTransform: 'initial',
  },
})

const SurveyStep = ({classes, lucidTerminate, lucidOverquota, lucidComplete}) => {
  const dispatch = useDispatch()
  const isMountedRef = useRef(true)

  const {imagesLoaded, videoLength, campaignVideo, RID} = useContext(SurveyContext)

  const [surveyState, setSurveyState] = useState(surveyStates.initializing)

  const [loadingAnswer, setLoadingAnswer] = useState(false)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [answerCount, setAnswerCount] = useState(0)
  const [selectedChoice, setSelectedChoice] = useState('')
  const [isValidAnswer, setIsValidAnswer] = useState(false)
  const [loadingImagesModal, openLoadingImagesModal, closeLoadingImagesModal] = useModalState(false)

  const createError = message => dispatch(createErrorAction(message))

  const surveyQuestions = useMemo(() => {
    return campaignVideo?.campaign?.surveyQuestionsDetail || []
  }, [campaignVideo])

  const totalSurveyQuestions = useMemo(() => {
    return campaignVideo?.campaign?.surveyQuestionsDetail?.length || 0
  }, [campaignVideo])

  const isUploadComplete = useMemo(() => {
    return imagesLoaded >= videoLength
  }, [imagesLoaded, videoLength])

  const hasSurveyQuestions = useMemo(() => {
    return surveyQuestions.length > 0
  }, [surveyQuestions])

  const completeSurvey = useCallback(() => {
    if (surveyState === surveyStates.completed) return

    tagManagerEvent(eventTypes.surveyEnded, {RID})
    setSurveyState(surveyStates.completed)
    lucidComplete()
  }, [surveyState, RID, lucidComplete])

  const terminateSurvey = useCallback(() => {
    if (surveyState === surveyStates.terminated) return

    setSurveyState(surveyStates.terminated)
    lucidTerminate()
  }, [surveyState, lucidTerminate])

  const waitForUploads = useCallback(() => {
    if (isUploadComplete) {
      completeSurvey()
    } else {
      tagManagerEvent(eventTypes.imageUploadModalStart, {
        RID,
        datetime: new Date().toJSON(),
      })
      setSurveyState(surveyStates.waitingForUploads)
      openLoadingImagesModal()
    }
  }, [isUploadComplete, completeSurvey, RID, openLoadingImagesModal])

  useEffect(() => {
    if (!campaignVideo || _.isEmpty(campaignVideo)) {
      if (surveyState !== surveyStates.terminated) {
        terminateSurvey()
      }
      return
    }

    switch (surveyState) {
      case surveyStates.initializing:
        if (hasSurveyQuestions) {
          setSurveyState(surveyStates.surveyLanding)
        } else {
          waitForUploads()
        }
        break

      case surveyStates.waitingForUploads:
        if (isUploadComplete) {
          tagManagerEvent(eventTypes.imageUploadModalEnd, {
            RID,
            datetime: new Date().toJSON(),
          })
          closeLoadingImagesModal()
          completeSurvey()
        }
        break

      case surveyStates.answeringQuestions: {
        const currentQuestion = surveyQuestions[currentQuestionIndex]
        if (_.isEmpty(currentQuestion)) {
          if (answerCount === surveyQuestions.length) {
            waitForUploads()
          } else {
            setSurveyState(surveyStates.failed)
          }
        }
        break
      }

      default:
        break
    }
  }, [
    campaignVideo,
    surveyState,
    hasSurveyQuestions,
    isUploadComplete,
    currentQuestionIndex,
    surveyQuestions,
    answerCount,
    RID,
    terminateSurvey,
    waitForUploads,
    completeSurvey,
    closeLoadingImagesModal,
  ])

  useEffect(() => {
    if (
      surveyState === surveyStates.answeringQuestions &&
      currentQuestionIndex === surveyQuestions.length - 1 &&
      loadingImagesModal &&
      isUploadComplete
    ) {
      tagManagerEvent(eventTypes.imageUploadModalEnd, {
        RID,
        datetime: new Date().toJSON(),
      })
      closeLoadingImagesModal()
      handleNextButtonClick()
    }
  }, [
    surveyState,
    currentQuestionIndex,
    surveyQuestions.length,
    loadingImagesModal,
    isUploadComplete,
    RID,
    closeLoadingImagesModal,
    handleNextButtonClick,
  ])

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const handleNextButtonClick = useCallback(async () => {
    const currentQuestion = surveyQuestions[currentQuestionIndex]

    if (!window.navigator.onLine) {
      createError('No network connection')
      return
    }

    if (currentQuestionIndex === surveyQuestions.length - 1 && !isUploadComplete) {
      tagManagerEvent(eventTypes.imageUploadModalStart, {
        RID,
        datetime: new Date().toJSON(),
      })
      openLoadingImagesModal()
      return
    }

    if (selectedChoice) {
      try {
        setLoadingAnswer(true)
        await VideoApi.saveSurveyQuestionAnswer(campaignVideo.campaign.id, {
          question: currentQuestion.question,
          answer: selectedChoice,
        })

        if (!isMountedRef.current) return

        tagManagerEvent(eventTypes.surveyAnswer, {
          RID,
          question: currentQuestion.question,
          answer: selectedChoice,
        })
        setAnswerCount(prevState => prevState + 1)
      } catch (error) {
        if (!isMountedRef.current) return

        const [message] = error?.response?.data?.['non_field_error'] || [error.message]

        if (error.response.status === 406) {
          createError(
            "Participation for this test has reached it's quota. Your participation has been discarded.",
          )
          tagManagerEvent(eventTypes.overquota, {RID})
          lucidOverquota()
        } else if (message !== 'User already answered this question') {
          // ignoring duplicated answer error
          tagManagerEvent(eventTypes.unexpectedError, {RID, errorMessage: error.message})
          createError(error.message)
          terminateSurvey()
        }

        return
      } finally {
        setLoadingAnswer(false)
      }
    }

    setSelectedChoice('')
    setCurrentQuestionIndex(prevState => prevState + 1)
  }, [
    surveyQuestions,
    currentQuestionIndex,
    isUploadComplete,
    selectedChoice,
    campaignVideo,
    RID,
    openLoadingImagesModal,
    lucidOverquota,
    terminateSurvey,
  ])

  const handleMultipleChoiceAnswer = choice => {
    setSelectedChoice(choice)
    setIsValidAnswer(choice !== '')
  }

  const handleOpenEndedAnswer = value => {
    setSelectedChoice(value)
    setIsValidAnswer(value.trim() !== '')
  }

  const handleFaultPercentageAnswer = (jsonString, isValid) => {
    setSelectedChoice(jsonString)
    setIsValidAnswer(isValid)
  }

  const handleSurveyStart = () => {
    setSurveyState(surveyStates.answeringQuestions)
  }

  if (surveyState === surveyStates.terminated) {
    return null
  }

  if (surveyState === surveyStates.completed) {
    return null
  }

  if (surveyState === surveyStates.failed) {
    return (
      <SurveyError
        lucidTerminate={() => {
          terminateSurvey()
        }}
      />
    )
  }

  if (surveyState === surveyStates.initializing || surveyState === surveyStates.surveyLanding) {
    return <SurveyLanding setSurveyStart={handleSurveyStart} />
  }

  if (surveyState === surveyStates.waitingForUploads) {
    return (
      <LoadingModal
        text="Please wait while we process your results. This could take a few minutes."
        open={loadingImagesModal}
        progress
      />
    )
  }

  if (surveyState === surveyStates.answeringQuestions) {
    const currentQuestion = surveyQuestions[currentQuestionIndex]

    if (_.isEmpty(currentQuestion) && surveyQuestions.length > 0) {
      return null
    }

    const getNextButtonText = () => {
      return currentQuestionIndex + 1 === totalSurveyQuestions ? 'Finish' : 'Next'
    }

    const getTooltipTitle = () => {
      if (!selectedChoice) {
        if (currentQuestion?.type === questionTypes.openEnded) {
          return 'Please write your answer before going to the next question'
        }
        return 'Please select an answer or meet the requirements from above before going to the next question.'
      }
      return ''
    }

    const getQuestion = () => {
      if (currentQuestion?.type === questionTypes.openEnded) {
        if (currentQuestion.subType === questionSubTypes.totalFaultPercentage) {
          return (
            <SurveyFaultPercentageAnswer
              question={currentQuestion}
              selectedChoice={selectedChoice}
              setSelectedChoice={handleFaultPercentageAnswer}
              setIsValidAnswer={setIsValidAnswer}
            />
          )
        }

        return (
          <SurveyOpenEndedAnswer
            question={currentQuestion}
            selectedChoice={selectedChoice}
            setSelectedChoice={handleOpenEndedAnswer}
          />
        )
      }

      if (currentQuestion?.type === questionTypes.multipleChoice) {
        return (
          <SurveyMultipleChoiceAnswer
            question={currentQuestion}
            selectedChoice={selectedChoice}
            setSelectedChoice={handleMultipleChoiceAnswer}
          />
        )
      }

      return null
    }

    return (
      <Grid className={classes.mainContainer}>
        <LoadingModal
          text="Please wait while we process your results. This could take a few minutes."
          open={loadingImagesModal}
          progress
        />
        <Grid>
          <Typography variant="h5" className={classes.title}>
            Survey
          </Typography>
        </Grid>
        {getQuestion()}
        <Grid style={{width: '100%'}}>
          <Tooltip interactive={false} title={getTooltipTitle()} placement="bottom">
            <span style={{display: 'block', height: '100%'}}>
              <Button
                disabled={!selectedChoice || !isValidAnswer || loadingAnswer}
                className={classes.nextButton}
                variant="contained"
                color="primary"
                fullWidth={false}
                onClick={handleNextButtonClick}
              >
                {loadingAnswer ? <CircularProgress size={24} /> : getNextButtonText()}
              </Button>
            </span>
          </Tooltip>
        </Grid>
        <Grid className={classes.surveyQuestionCounter}>
          Question {currentQuestionIndex + 1} of {totalSurveyQuestions}
        </Grid>
      </Grid>
    )
  }

  return null
}

export default withStyles(styles)(SurveyStep)
